import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api';
import LoadingSpinner from '../components/LoadingSpinner';
import Toast, { useToast } from '../components/Toast';

// ============================================================================
// CashReceiptPage — customer cash receipt (현금영수증) request
// Route: /r/:sessionId/cash-receipt  (suggested; wire up in App.js)
// ============================================================================

const formatWon = (n) => `${Number(n || 0).toLocaleString('ko-KR')}원`;

const formatDateTime = (iso) => {
  if (!iso) return '-';
  try {
    const d = new Date(iso.includes('T') ? iso : iso.replace(' ', 'T') + 'Z');
    if (isNaN(d.getTime())) return iso;
    return d.toLocaleString('ko-KR', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  } catch { return iso; }
};

// Format phone input as 010-XXXX-XXXX while typing
function formatPhoneInput(v) {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

function isValidPhone(v) {
  const d = (v || '').replace(/\D/g, '');
  return /^01[016789]\d{7,8}$/.test(d);
}

export default function CashReceiptPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [payments, setPayments] = useState([]);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [phone, setPhone] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [issued, setIssued] = useState(null); // { receiptNumber, amount, phone }

  const load = useCallback(async () => {
    if (!sessionId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await api.get(`/sessions/${sessionId}/payment-status`);
      const data = res?.data || res;
      const list = (data?.payments || []).filter((p) => p.status === 'completed');
      setPayments(list);
      if (list.length > 0) setSelectedPaymentId(list[0].id);

      // Try to restore phone from localStorage
      const savedPhone = localStorage.getItem('customer_phone');
      if (savedPhone) setPhone(formatPhoneInput(savedPhone));
    } catch {
      showToast('정보를 불러오지 못했습니다', 'error');
    } finally {
      setLoading(false);
    }
  }, [sessionId, showToast]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!selectedPaymentId) {
      showToast('결제 내역을 선택해주세요', 'error');
      return;
    }
    if (!isValidPhone(phone)) {
      showToast('휴대폰 번호를 올바르게 입력해주세요', 'error');
      return;
    }
    setIsSubmitting(true);
    try {
      const digits = phone.replace(/\D/g, '');
      const res = await api.post('/receipts/cash', {
        payment_id: selectedPaymentId,
        identifier_type: 'phone',
        identifier: digits,
      });
      const data = res?.data || res;
      localStorage.setItem('customer_phone', digits);
      const selected = payments.find((p) => p.id === selectedPaymentId);
      setIssued({
        receiptNumber: data?.receipt?.receipt_number,
        amount: selected?.amount || 0,
        phone,
      });
      showToast('현금영수증이 발행되었습니다', 'success');
    } catch (err) {
      showToast(err.message || '발행에 실패했습니다', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg, #f5f5f5)', paddingBottom: 80 }}>
      <Toast toast={toast} />

      {/* Header */}
      <div
        style={{
          background: 'white',
          padding: '16px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          borderBottom: '1px solid var(--border, #eee)',
          position: 'sticky',
          top: 0,
          zIndex: 10,
        }}
      >
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', padding: 6, cursor: 'pointer', color: 'var(--text, #111)' }}
          aria-label="뒤로"
        >
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
        </button>
        <h1 style={{ fontSize: 17, fontWeight: 700, margin: 0 }}>현금영수증 발행</h1>
      </div>

      <div style={{ padding: 16 }}>
        {issued ? (
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: 24,
              textAlign: 'center',
              border: '2px solid var(--primary)',
            }}
          >
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: 'var(--primary-light, #fff9e6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                color: 'var(--primary)',
              }}
            >
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>발행 완료</div>
            <div style={{ fontSize: 13, color: 'var(--text-tertiary, #888)', marginBottom: 20 }}>
              현금영수증이 정상적으로 발행되었습니다
            </div>
            <div
              style={{
                background: 'var(--bg, #f5f5f5)',
                borderRadius: 12,
                padding: 16,
                textAlign: 'left',
                fontSize: 14,
              }}
            >
              <Row label="금액" value={<strong>{formatWon(issued.amount)}</strong>} />
              <Row label="휴대폰" value={issued.phone} />
              {issued.receiptNumber && (
                <Row
                  label="영수증 번호"
                  value={<span style={{ fontFamily: 'monospace', fontSize: 12 }}>{issued.receiptNumber}</span>}
                />
              )}
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button
                onClick={() => setIssued(null)}
                style={{
                  flex: 1,
                  padding: 14,
                  background: 'var(--bg, #f5f5f5)',
                  color: 'var(--text, #111)',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                다시 발행
              </button>
              <button
                onClick={() => navigate(-1)}
                style={{
                  flex: 1,
                  padding: 14,
                  background: 'var(--primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: 12,
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: 'pointer',
                }}
              >
                확인
              </button>
            </div>
          </div>
        ) : payments.length === 0 ? (
          <div
            style={{
              background: 'white',
              borderRadius: 16,
              padding: '40px 20px',
              textAlign: 'center',
              color: 'var(--text-tertiary, #999)',
            }}
          >
            완료된 결제가 없어 영수증을 발행할 수 없습니다.
          </div>
        ) : (
          <>
            <div style={{ background: 'white', borderRadius: 16, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>발행할 결제 선택</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {payments.map((p) => {
                  const active = selectedPaymentId === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setSelectedPaymentId(p.id)}
                      style={{
                        padding: '14px 16px',
                        border: active ? '2px solid var(--primary)' : '1px solid var(--border, #e5e5e5)',
                        background: active ? 'var(--primary-light, #fff9e6)' : 'white',
                        borderRadius: 12,
                        cursor: 'pointer',
                        textAlign: 'left',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 12, color: 'var(--text-tertiary, #888)' }}>
                          {formatDateTime(p.created_at)}
                        </span>
                        <span style={{ fontSize: 16, fontWeight: 700, color: active ? 'var(--primary)' : 'var(--text, #111)' }}>
                          {formatWon(p.amount)}
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ background: 'white', borderRadius: 16, padding: 16, marginBottom: 12 }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>휴대폰 번호</div>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="010-0000-0000"
                value={phone}
                onChange={(e) => setPhone(formatPhoneInput(e.target.value))}
                style={{
                  width: '100%',
                  padding: '14px 16px',
                  border: '1px solid var(--border, #e5e5e5)',
                  borderRadius: 12,
                  fontSize: 16,
                  outline: 'none',
                }}
              />
              <div style={{ fontSize: 12, color: 'var(--text-tertiary, #888)', marginTop: 8 }}>
                소득공제용 현금영수증이 등록된 번호로 발행됩니다.
              </div>
            </div>

            <button
              onClick={submit}
              disabled={isSubmitting || !isValidPhone(phone)}
              style={{
                width: '100%',
                padding: 16,
                background: !isValidPhone(phone) ? 'var(--border, #e5e5e5)' : 'var(--primary)',
                color: 'white',
                border: 'none',
                borderRadius: 14,
                fontSize: 15,
                fontWeight: 700,
                cursor: isValidPhone(phone) ? 'pointer' : 'not-allowed',
                opacity: isSubmitting ? 0.6 : 1,
              }}
            >
              {isSubmitting ? '발행 중...' : '현금영수증 발행'}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0' }}>
    <span style={{ color: 'var(--text-tertiary, #888)' }}>{label}</span>
    <span>{value}</span>
  </div>
);
